import { useEffect, useRef, useState } from 'react';

import {
  CONTEXT_MENU_WIDTH,
  contextMenuPosition,
  type ContextMenuModel,
} from '../model/context-menu.js';
import type { ContextMenuState } from '../state/store.js';

/**
 * The context menu primitive (design 1616–1633).
 *
 * One menu, four subjects. This phase owns the primitive and Phases 3–5 are its
 * callers, so it knows nothing about what its items *do*: it takes a model built
 * by `model/context-menu.ts` and emits the chosen item's id.
 *
 * A full-viewport backdrop catches the click that dismisses it — including a
 * second right-click, which is what a person does when they opened it on the
 * wrong row. Escape closes it before it closes anything else, which is the
 * design's "unwind one layer at a time".
 *
 * ## It is a menu by role, so it is a menu by keyboard
 *
 * It declared `role="menu"` with `role="menuitem"` children from Phase 2 and had
 * no focus management and no arrow keys, which is the worst of both readings: a
 * screen reader announces a menu and then the keys a menu implies do nothing.
 * The first item takes focus on open, Up/Down wrap through the items, Home/End
 * jump to the ends, and Escape still unwinds exactly one layer.
 *
 * The items are a roving tabindex rather than nine tab stops, because a menu is
 * one stop in the page's tab ring and its own thing inside.
 */

export interface ContextMenuProps {
  readonly menu: ContextMenuState;
  readonly model: ContextMenuModel;
  readonly onChoose: (itemId: string) => void;
  readonly onClose: () => void;
}

export function ContextMenu(props: ContextMenuProps): JSX.Element {
  const { onClose } = props;

  const itemCount = props.model.items.length;
  const [active, setActive] = useState(0);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    itemRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        // Stop the shell's own Escape handler closing something behind it.
        event.stopPropagation();
        onClose();
        return;
      }
      /*
       * Tab wraps inside the menu rather than leaving it.
       *
       * This is a `role="menu"` over a backdrop: one Tab used to move focus to the
       * hotkeys toggle *behind* it, leaving an open menu with focus somewhere the
       * operator cannot see and a click target they cannot reach without closing it.
       * A menu owns the tab ring for as long as it is open; Escape is the way out,
       * and it is already handled above.
       */
      if (event.key === 'Tab') {
        if (itemCount === 0) return;
        event.preventDefault();
        event.stopPropagation();
        setActive((current) => {
          const next = (current + (event.shiftKey ? -1 : 1) + itemCount) % itemCount;
          itemRefs.current[next]?.focus();
          return next;
        });
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
      if (step === 0 && event.key !== 'Home' && event.key !== 'End') return;
      if (itemCount === 0) return;
      event.preventDefault();
      event.stopPropagation();
      setActive((current) => {
        const next =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? itemCount - 1
              : (current + step + itemCount) % itemCount;
        itemRefs.current[next]?.focus();
        return next;
      });
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, itemCount]);

  const position = contextMenuPosition(props.menu, props.model.items.length, {
    width: typeof window === 'undefined' ? 1920 : window.innerWidth,
    height: typeof window === 'undefined' ? 1080 : window.innerHeight,
  });

  return (
    <div
      data-testid="context-menu-backdrop"
      onClick={onClose}
      onContextMenu={(event) => {
        event.preventDefault();
        onClose();
      }}
      style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-menu)' }}
    >
      <div
        data-testid="context-menu"
        data-subject={props.menu.subject}
        role="menu"
        aria-label={props.model.title}
        onClick={(event) => event.stopPropagation()}
        style={{
          position: 'fixed',
          left: position.left,
          top: position.top,
          width: CONTEXT_MENU_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface)',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            flex: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            padding: '7px 10px 6px',
            background: 'var(--surface-2)',
            borderBottom: '1px solid var(--border-strong)',
          }}
        >
          <span
            data-testid="context-menu-title"
            style={{
              fontFamily: 'var(--font-data)',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '.06em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: props.model.titleColour,
            }}
          >
            {props.model.title}
          </span>
          {props.model.subtitle !== '' && (
            <span
              style={{
                fontFamily: 'var(--font-prose)',
                fontSize: 11,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'var(--text-muted)',
              }}
            >
              {props.model.subtitle}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', padding: '3px 0' }}>
          {props.model.items.map((item, index) => (
            <button
              key={item.id}
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              type="button"
              role="menuitem"
              tabIndex={index === active ? 0 : -1}
              data-testid={'context-item-' + item.id}
              onFocus={() => setActive(index)}
              onClick={() => {
                props.onChoose(item.id);
                onClose();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                height: 26,
                padding: '0 10px',
                background: 'transparent',
                border: 0,
                borderBottom:
                  '1px solid ' + (item.separator === true ? 'var(--rule)' : 'transparent'),
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: 'var(--font-ui)',
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: item.danger === true ? 'var(--red-text)' : 'var(--text-2)',
                }}
              >
                {item.label}
              </span>
              {item.hint !== undefined && (
                <span
                  style={{
                    flex: 'none',
                    fontFamily: 'var(--font-data)',
                    fontSize: 10,
                    letterSpacing: '.08em',
                    whiteSpace: 'nowrap',
                    color: 'var(--text-disabled)',
                  }}
                >
                  {item.hint}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
